import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  ExternalLink,
  Loader2,
  Cpu,
  CalendarClock,
  ShieldOff,
  CircleDot,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CFG = {
  end_of_support: {
    label: "FIN DE SUPPORT",
    cls: "bg-[#E4002B] text-white",
    ring: "border-l-[#E4002B]",
    Icon: ShieldOff,
  },
  end_of_order: {
    label: "FIN DE COMMANDE",
    cls: "bg-[#D4AF37] text-[#0A1326]",
    ring: "border-l-[#D4AF37]",
    Icon: CalendarClock,
  },
  supported: {
    label: "SUPPORTÉ",
    cls: "bg-[#00A88E] text-white",
    ring: "border-l-[#00A88E]",
    Icon: CircleDot,
  },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function LifecycleCard({ item }) {
  const cfg = STATUS_CFG[item.status] || STATUS_CFG.supported;
  const { Icon } = cfg;
  return (
    <article
      data-testid={`hw-${item.status}`}
      className={`group hk-card relative bg-[#142042]/60 border border-[#D4AF37]/15 border-l-4 ${cfg.ring} p-6 rounded-[2px] flex flex-col h-full`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <span
          className={`text-[10px] font-bold tracking-[0.18em] uppercase px-2 py-1 rounded-[2px] flex items-center gap-1.5 ${cfg.cls}`}
        >
          <Icon size={11} />
          {cfg.label}
        </span>
        {item.category && (
          <span className="text-[10px] text-[#F5F5F5]/50 uppercase tracking-[0.18em] font-semibold">
            {item.category}
          </span>
        )}
      </div>

      <div className="flex items-start gap-3">
        <Cpu size={22} className="text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={1.6} />
        <h3 className="font-mono text-[15px] sm:text-[16px] font-bold text-[#F5F5F5] tracking-tight break-all leading-tight">
          {item.product}
        </h3>
      </div>

      <dl className="mt-5 space-y-2 text-[12px] flex-1">
        <div className="flex items-center justify-between gap-3 border-b border-[#D4AF37]/8 pb-1.5">
          <dt className="text-[#F5F5F5]/50 uppercase tracking-[0.14em] text-[10px] font-semibold">
            Fin de commande
          </dt>
          <dd className="text-[#F5F5F5]/85 font-mono">{fmtDate(item.end_of_order)}</dd>
        </div>
        <div className="flex items-center justify-between gap-3 border-b border-[#D4AF37]/8 pb-1.5">
          <dt className="text-[#F5F5F5]/50 uppercase tracking-[0.14em] text-[10px] font-semibold">
            Extension service
          </dt>
          <dd className="text-[#F5F5F5]/85 font-mono">{fmtDate(item.last_service_extension)}</dd>
        </div>
        <div className="flex items-center justify-between gap-3 border-b border-[#D4AF37]/8 pb-1.5">
          <dt className="text-[#F5F5F5]/50 uppercase tracking-[0.14em] text-[10px] font-semibold">
            Fin de support
          </dt>
          <dd className="text-[#D4AF37] font-mono font-bold">{fmtDate(item.end_of_support)}</dd>
        </div>
        {item.last_supported_software_version && (
          <div className="flex items-center justify-between gap-3">
            <dt className="text-[#F5F5F5]/50 uppercase tracking-[0.14em] text-[10px] font-semibold">
              Dernier firmware
            </dt>
            <dd className="text-[#F5F5F5]/85 font-mono text-[11px]">
              {item.last_supported_software_version}
            </dd>
          </div>
        )}
      </dl>
    </article>
  );
}

export default function Threats() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetchData = async () => {
      try {
        const { data } = await axios.get(
          `${API}/threats/hardware-lifecycle?limit=8`
        );
        if (!alive) return;
        setData(data);
        setError(null);
      } catch (e) {
        if (!alive) return;
        setError("Flux temporairement indisponible.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchData();
    // Refresh every 3 hours
    const interval = setInterval(fetchData, 3 * 60 * 60 * 1000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, []);

  const items = data?.items || [];

  return (
    <section
      id="menaces"
      data-testid="threats-section"
      className="relative bg-[#050a18] py-20 md:py-28 overflow-hidden border-t border-[#D4AF37]/15"
    >
      <div className="absolute inset-0 dot-grid opacity-[0.06] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-5 md:px-8 lg:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="relative inline-flex h-2.5 w-2.5">
                <span className="absolute inset-0 rounded-full bg-[#D4AF37] dot-pulse" />
                <span className="absolute inset-0 rounded-full bg-[#D4AF37]" />
              </span>
              <span className="overline text-[#D4AF37] tracking-[0.22em]">
                ── Veille cycle de vie
              </span>
            </div>
            <h2 className="font-display text-[28px] sm:text-4xl md:text-5xl font-black tracking-tight text-[#F5F5F5] leading-[1.05] max-w-3xl">
              Fin de support{" "}
              <span className="text-[#D4AF37]">matériel Fortinet</span>.
            </h2>
            <p className="mt-4 text-[#F5F5F5]/65 text-[14px] sm:text-[15px] max-w-2xl leading-relaxed">
              Suivez en temps réel les dates de fin de commande, d'extension de
              service et de fin de support des équipements{" "}
              <strong className="text-[#F5F5F5]">Fortinet</strong>. Anticipez
              vos renouvellements et sécurisez votre infrastructure.
            </p>
          </div>

          <div className="flex items-center gap-2 text-[11px] text-[#F5F5F5]/50">
            <Cpu size={14} className="text-[#D4AF37]" />
            <span className="uppercase tracking-[0.18em] font-semibold">
              Source : Fortinet Support
            </span>
          </div>
        </div>

        {/* Content */}
        {loading && (
          <div className="flex items-center justify-center py-20 text-[#F5F5F5]/55 gap-2">
            <Loader2 className="animate-spin" size={18} />
            Chargement du flux Fortinet…
          </div>
        )}

        {!loading && error && items.length === 0 && (
          <div className="flex items-center gap-3 p-6 border border-dashed border-[#D4AF37]/25 rounded-[2px] bg-[#142042]/40 text-[#F5F5F5]/65">
            <AlertTriangle size={18} className="text-[#D4AF37]" />
            <span>{error}</span>
          </div>
        )}

        {!loading && items.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {items.map((item, i) => (
                <LifecycleCard key={item.product + i} item={item} />
              ))}
            </div>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[12px] text-[#F5F5F5]/50">
              <div className="flex items-center gap-2">
                <CalendarClock size={14} className="text-[#D4AF37]" />
                <span>
                  {items.length} produits affichés
                  {data?.fetched_at && (
                    <>
                      {" · Dernière synchro "}
                      {new Date(data.fetched_at).toLocaleTimeString("fr-FR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </>
                  )}
                </span>
              </div>
              <a
                href="https://support.fortinet.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="threats-source-link"
                className="inline-flex items-center gap-1.5 hover:text-[#D4AF37] transition-colors"
              >
                Voir tout le catalogue sur Fortinet Support
                <ExternalLink size={12} />
              </a>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
